// lib/media.ts — job photos as real files (server/src/media.js).
//
// A work-order photo used to be a 480px JPEG data URL stored inside the work
// order. The website builder can't put a data: URL on a public page, so a
// contractor's own jobs never reached their website. Now:
//
//   uploadPhoto(file)   decode (EXIF orientation applied), downscale to a
//                       2000px long edge, JPEG ~0.82, POST /media/upload →
//                       { url, w, h }. Offline, or the upload fails → today's
//                       480px data URL, marked pending: true.
//   startMediaSync()    runs in the app while someone is signed in: pending
//                       photos, and the old base64 thumbnails, are uploaded
//                       quietly in the background — a few a minute, online
//                       only — and their url rewritten in state, so the diff
//                       sync saves just the changed jobs. Idempotent: each is
//                       uploaded under a hash of its data URL, so a retry, a
//                       reload or a second device lands on the same file.
//
// The photo keeps its shape — { name, url, at, tag } plus optional w, h,
// pending and web (web: false = "Don't use on my website") — so every reader
// (hub gallery, crew mode, timeline, automations, the website) keeps working.
// No imports: the pure half is unit-tested in node (scripts/unit-media.mjs).

export type JobPhoto = {
  name: string; url: string; at: number; tag?: "before" | "during" | "after" | null;
  w?: number; h?: number; pending?: boolean; web?: boolean; [k: string]: any;
};

export const FULL_EDGE = 2000;
export const FULL_QUALITY = 0.82;
export const THUMB_EDGE = 480;
export const THUMB_QUALITY = 0.7;
export const UPLOAD_TIMEOUT_MS = 25000;

/* ============================================================
   Pure
   ============================================================ */

/* w×h scaled so the long edge is at most `max` — never up. */
export function fitWithin(w: number, h: number, max: number): { w: number; h: number } {
  if (!(w > 0 && h > 0)) return { w: 0, h: 0 };
  const s = Math.min(1, max / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
}

export const isDataUrl = (u: any) => /^data:/i.test(String(u || ""));
export const isHosted = (u: any) => /^https?:\/\//i.test(String(u || ""));
/* a photo still living inside the job as an image data URL */
export const needsUpload = (p: any) => !!(p && /^data:image\/(jpeg|jpg|png|webp);base64,/i.test(String(p.url || "")));

/* The photo once it's hosted: the new url and the uploaded image's size;
   `pending` goes, everything else (name, at, tag, web…) stays. */
export function hostedPhoto(p: JobPhoto, up: { url: string; width?: number | null; height?: number | null }, size?: { w: number; h: number }): JobPhoto {
  const { pending, ...rest } = p || ({} as any);
  const w = Number(up.width) || (size && size.w) || rest.w;
  const h = Number(up.height) || (size && size.h) || rest.h;
  const out: JobPhoto = { ...rest, url: up.url };
  if (w && h) { out.w = w; out.h = h; }
  return out;
}

/* The fallback: today's small data URL, flagged so the sync retries it. */
export function pendingPhoto(base: { name: string; at: number; tag?: any }, thumb: string, w: number, h: number, pending = true): JobPhoto {
  const out: JobPhoto = { ...base, url: thumb };
  if (w && h) { out.w = w; out.h = h; }
  if (pending) out.pending = true;
  return out;
}

/* "Don't use on my website": web:false when off; the key goes away when on,
   so a photo that was never toggled looks exactly like it did before. */
export function toggleWeb(p: JobPhoto): JobPhoto {
  const { web, ...rest } = p;
  return web === false ? rest as JobPhoto : { ...rest, web: false };
}

/* Every photo in every job whose url is `from` → `to` (with its size); the
   same array back when nothing matched, so an unchanged job isn't re-saved. */
export function replacePhotoUrl(photos: any, from: string, up: { url: string; width?: number | null; height?: number | null }): any[] {
  const list = Array.isArray(photos) ? photos : [];
  let hit = false;
  const next = list.map((p) => { if (p && p.url === from) { hit = true; return hostedPhoto(p, up); } return p; });
  return hit ? next : list;
}

/* A quote's saved floor previews (coatpreview.tsx Visual): the render, and the
   before photo, once hosted — `pending` goes with the render's data URL. The
   same array back when nothing matched. */
export function replaceVisualUrl(visuals: any, from: string, up: { url: string }): any[] {
  const list = Array.isArray(visuals) ? visuals : [];
  let hit = false;
  const next = list.map((v) => {
    if (!v) return v;
    if (v.url === from) { hit = true; const { pending, ...rest } = v; return { ...rest, url: up.url }; }
    if (v.before === from) { hit = true; return { ...v, before: up.url }; }
    return v;
  });
  return hit ? next : list;
}

/* The photos the background sync should upload, pending (new) ones first,
   each data URL once — the same photo copied onto two jobs uploads once.
   A quote's floor previews rendered offline (coatpreview.tsx) count too: the
   customer's page shows hosted pictures only. */
export function photosToUpload(state: any): { url: string; at: number; pending: boolean }[] {
  const seen = new Map<string, { url: string; at: number; pending: boolean }>();
  for (const wo of (state && state.workOrders) || []) {
    for (const p of (wo && Array.isArray(wo.photos) ? wo.photos : [])) {
      if (!needsUpload(p)) continue;
      const cur = seen.get(p.url);
      if (!cur) seen.set(p.url, { url: p.url, at: Number(p.at) || 0, pending: !!p.pending });
      else if (p.pending) cur.pending = true;
    }
  }
  for (const q of (state && state.quotes) || []) {
    for (const v of (q && Array.isArray(q.visuals) ? q.visuals : [])) {
      for (const u of [v && v.url, v && v.before]) {
        if (!needsUpload({ url: u })) continue;
        const pend = !!(v.pending || u === v.url);   // a render that never uploaded is what the customer is waiting on
        const cur = seen.get(u);
        if (!cur) seen.set(u, { url: u, at: Number(v.at) || 0, pending: pend });
        else if (pend) cur.pending = true;
      }
    }
  }
  return [...seen.values()].sort((a, b) => (Number(b.pending) - Number(a.pending)) || (b.at - a.at));
}

/* 30 s, 1, 2, 4 … minutes, capped at an hour. */
export function backoffMs(failures: number): number {
  return Math.min(3600000, 30000 * Math.pow(2, Math.max(0, failures - 1)));
}

/* The year a photo's file goes under (so a retried upload keeps its path). */
export function photoYear(at: any, now = new Date()): number {
  const t = Number(at);
  const y = t > 0 ? new Date(t).getUTCFullYear() : now.getUTCFullYear();
  return y >= 2000 && y <= now.getUTCFullYear() + 1 ? y : now.getUTCFullYear();
}

/* data:image/jpeg;base64,… → Blob */
export function dataUrlToBlob(dataUrl: string): Blob {
  const m = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(String(dataUrl || ""));
  if (!m) throw new Error("not a data URL");
  const bytes = m[2] ? atob(m[3]) : decodeURIComponent(m[3]);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: m[1] });
}

/* A stable hex key for a data URL: SHA-256 where the browser has it (every
   https page), else four FNV-1a passes — a device without it just makes its
   own copy, never a wrong one. */
export function fnvHex(text: string): string {
  let out = "";
  for (let seed = 0; seed < 4; seed++) {
    let h = (0x811c9dc5 ^ (seed * 0x9e3779b1)) >>> 0;
    for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    out += h.toString(16).padStart(8, "0");
  }
  return out;
}
export async function hashKey(text: string): Promise<string> {
  try {
    const c: any = (globalThis as any).crypto;
    if (c && c.subtle && typeof TextEncoder !== "undefined") {
      const buf = await c.subtle.digest("SHA-256", new TextEncoder().encode(text));
      return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (e) { /* fall through */ }
  return fnvHex(text);
}

/* Is it worth retrying? 4xx other than 408/429 means this photo will never
   upload (not an image we take, too big); 401/403 means stop for now. */
export function failureKind(e: any): "retry" | "skip" | "stop" {
  const s = Number(e && e.status);
  if (s === 401 || s === 403) return "stop";
  if (s >= 400 && s < 500 && s !== 408 && s !== 429) return "skip";
  return "retry";
}

/* ============================================================
   Browser: decode, downscale, upload
   ============================================================ */
const W: any = typeof window !== "undefined" ? window : {};

type Decoded = { src: any; w: number; h: number; close: () => void };
/* EXIF orientation applied: createImageBitmap's imageOrientation, else an
   <img> (browsers honour EXIF on <img> by default). */
export async function decodeImage(file: Blob): Promise<Decoded> {
  if (typeof (globalThis as any).createImageBitmap === "function") {
    try {
      const bmp: any = await (globalThis as any).createImageBitmap(file, { imageOrientation: "from-image" });
      return { src: bmp, w: bmp.width, h: bmp.height, close: () => { try { bmp.close(); } catch (e) { /* ignore */ } } };
    } catch (e) { /* older Safari: the options bag or the format — try an <img> */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("That file isn't a photo we can read."));
      im.src = url;
    });
    return { src: img, w: img.naturalWidth, h: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) { URL.revokeObjectURL(url); throw e; }
}

function draw(d: Decoded, max: number) {
  const size = fitWithin(d.w, d.h, max);
  const c = document.createElement("canvas");
  c.width = size.w; c.height = size.h;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.drawImage(d.src, 0, 0, size.w, size.h);
  return { c, ...size };
}

export type Prepared = { full: Blob; fw: number; fh: number; thumb: string; tw: number; th: number };
export async function preparePhoto(file: Blob): Promise<Prepared> {
  const d = await decodeImage(file);
  try {
    const big = draw(d, FULL_EDGE);
    const full: Blob = await new Promise((resolve, reject) => big.c.toBlob((b) => (b ? resolve(b) : reject(new Error("couldn't encode"))), "image/jpeg", FULL_QUALITY));
    const small = draw(d, THUMB_EDGE);
    return { full, fw: big.w, fh: big.h, thumb: small.c.toDataURL("image/jpeg", THUMB_QUALITY), tw: small.w, th: small.h };
  } finally { d.close(); }
}

const demo = () => !!(W.VFP_DEMO && W.VFP_DEMO.active);
/* a service we could upload to: configured, not the sandbox */
export const uploadsConfigured = () => !!(W.VFP_API_BASE && W.VFP_DB && W.VFP_DB.mediaUpload) && !demo();
const online = () => typeof navigator === "undefined" || navigator.onLine !== false;

/* full-size copies of photos that fell back to the thumbnail, this session
   only — so a retry uploads the real photo, not the 480px stand-in */
const fullCopies = new Map<string, Blob>();

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(Object.assign(new Error("upload timed out"), { status: 408 })), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

/* One picked file → the url to store on the photo and its size. */
export async function uploadPhoto(file: File, opts: { timeoutMs?: number } = {}): Promise<{ url: string; w: number; h: number; pending?: true }> {
  const prep = await preparePhoto(file);
  if (!uploadsConfigured()) return { url: prep.thumb, w: prep.tw, h: prep.th };   // local mode / sandbox: as before
  const fallback = () => { fullCopies.set(prep.thumb, prep.full); return { url: prep.thumb, w: prep.tw, h: prep.th, pending: true as const }; };
  if (!online()) return fallback();
  try {
    const r = await withTimeout<any>(W.VFP_DB.mediaUpload(prep.full, {}), opts.timeoutMs || UPLOAD_TIMEOUT_MS);
    if (!r || !isHosted(r.url)) return fallback();
    return { url: r.url, w: Number(r.width) || prep.fw, h: Number(r.height) || prep.fh };
  } catch (e) { return fallback(); }
}

/* ============================================================
   Background sync (app.tsx starts it while someone is signed in)
   ============================================================ */
const LEDGER_KEY = "fw_media_uploaded";   // hash → hosted url, so a reload doesn't re-upload
function ledgerGet(key: string): any {
  try { const m = JSON.parse(localStorage.getItem(LEDGER_KEY) || "{}"); return m[key] || null; } catch (e) { return null; }
}
function ledgerSet(key: string, v: any) {
  try {
    const m = JSON.parse(localStorage.getItem(LEDGER_KEY) || "{}");
    m[key] = v;
    const keys = Object.keys(m);
    if (keys.length > 3000) for (const k of keys.slice(0, keys.length - 3000)) delete m[k];
    localStorage.setItem(LEDGER_KEY, JSON.stringify(m));
  } catch (e) { /* a full device just re-asks the server, which answers with the same file */ }
}

export type SyncDeps = {
  getState: () => any;
  updateWorkOrder: (id: string, patch: any) => void;
  updateQuote?: (id: string, patch: any) => void;   // a quote's floor previews (coatpreview.tsx) get rewritten too
  ready?: () => boolean;            // e.g. the cloud copy has loaded
  notify?: (msg: string) => void;   // said once, only after repeated failures
  tickMs?: number;
  perTick?: { pending: number; old: number };
};

export function startMediaSync(deps: SyncDeps): () => void {
  const tickMs = deps.tickMs || 15000;
  const perTick = deps.perTick || { pending: 3, old: 1 };   // old thumbnails: 4 a minute
  const fails = new Map<string, { n: number; next: number }>();
  const skip = new Set<string>();
  let stopped = false, busy = false, stopUntil = 0, told = false;

  const rewrite = (from: string, up: any) => {
    const st = deps.getState() || {};
    for (const wo of st.workOrders || []) {
      if (!wo || !Array.isArray(wo.photos)) continue;
      const next = replacePhotoUrl(wo.photos, from, up);
      if (next !== wo.photos) deps.updateWorkOrder(wo.id, { photos: next });
    }
    if (deps.updateQuote) for (const q of st.quotes || []) {
      if (!q || !Array.isArray(q.visuals)) continue;
      const next = replaceVisualUrl(q.visuals, from, up);
      if (next !== q.visuals) deps.updateQuote(q.id, { visuals: next });
    }
  };

  const one = async (item: { url: string; at: number; pending: boolean }) => {
    const key = await hashKey(item.url);
    const known = ledgerGet(key);
    if (known && isHosted(known.url)) { rewrite(item.url, known); fullCopies.delete(item.url); return; }
    const blob = fullCopies.get(item.url) || dataUrlToBlob(item.url);
    try {
      const r = await withTimeout<any>(W.VFP_DB.mediaUpload(blob, { key, year: photoYear(item.at) }), UPLOAD_TIMEOUT_MS * 2);
      if (!r || !isHosted(r.url)) throw Object.assign(new Error("no url"), { status: 502 });
      const up = { url: r.url, width: r.width || null, height: r.height || null };
      ledgerSet(key, up);
      rewrite(item.url, up);
      fullCopies.delete(item.url);
      fails.delete(item.url);
    } catch (e: any) {
      const kind = failureKind(e);
      if (kind === "stop") { stopUntil = Date.now() + 10 * 60000; return; }
      if (kind === "skip") { skip.add(item.url); return; }
      if (Number(e && e.status) === 429) { stopUntil = Date.now() + 5 * 60000; return; }
      const f = fails.get(item.url) || { n: 0, next: 0 };
      f.n++; f.next = Date.now() + backoffMs(f.n);
      fails.set(item.url, f);
      if (f.n >= 5 && !told && deps.notify) { told = true; deps.notify("Some job photos haven't uploaded yet. They're still saved on the job, and we'll keep trying."); }
    }
  };

  const tick = async () => {
    if (stopped || busy || !online() || !uploadsConfigured() || Date.now() < stopUntil) return;
    if (deps.ready && !deps.ready()) return;
    busy = true;
    try {
      const now = Date.now();
      const due = photosToUpload(deps.getState()).filter((x) => !skip.has(x.url) && !((fails.get(x.url) || { next: 0 }).next > now));
      const batch = [...due.filter((x) => x.pending).slice(0, perTick.pending), ...due.filter((x) => !x.pending).slice(0, perTick.old)];
      for (const item of batch) { if (stopped || Date.now() < stopUntil) break; await one(item); }
    } catch (e) { /* never let the loop die */ }
    finally { busy = false; }
  };

  const t = setInterval(tick, tickMs);
  const onOnline = () => { stopUntil = 0; tick(); };
  if (typeof window !== "undefined" && window.addEventListener) window.addEventListener("online", onOnline);
  setTimeout(tick, Math.min(5000, tickMs));
  return () => {
    stopped = true;
    clearInterval(t);
    if (typeof window !== "undefined" && window.removeEventListener) window.removeEventListener("online", onOnline);
  };
}

/* ============================================================
   Job documents (a permit copy) → the private bucket
   ============================================================ */
export const docUploadsConfigured = () => !!(W.VFP_API_BASE && W.VFP_DB && W.VFP_DB.mediaDocUpload) && !demo() && online();

/* → { path, type, bytes }; throws with the server's message */
export async function storeDocument(file: File): Promise<{ path: string; type: string; bytes: number }> {
  return withTimeout<any>(W.VFP_DB.mediaDocUpload(file), UPLOAD_TIMEOUT_MS * 2);
}

/* a short-lived link to read (or download) a stored document */
export async function documentUrl(path: string, opts: { download?: boolean; name?: string } = {}): Promise<string> {
  const r = await W.VFP_DB.mediaDocUrl(path, opts);
  if (!r || !isHosted(r.url)) throw new Error("No link came back — try again.");
  return r.url;
}
